import React, { useState } from "react";
import { motion } from "framer-motion";

const NETWORK_IMG =
  "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=1600&q=85";

const tabs = [
  {
    id: "reach",
    label: "Targeted Reach",
    body:
      "Our network is engineered to facilitate exceptional encounters between high-end brands and their natural audience. Premium placements in elite venues ensure your message reaches the consumers who set the cultural tempo.",
    points: [
      "Curated UHNI/HNI access",
      "Premium-only environments",
      "Verified, high-context exposure",
    ],
  },
  {
    id: "benefits",
    label: "Benefits of PrDOOH",
    body:
      "Programmatic flexibility meets the credibility of traditional out-of-home. Schedule, segment, and adapt creative in real time — without losing the dignity of the placement.",
    points: [
      "Dynamic creative & dayparting",
      "Brand-safe, premium inventory",
      "Optimised cost-per-attention",
    ],
  },
  {
    id: "measured",
    label: "A Measured Approach",
    body:
      "We treat every campaign like a private commission. Setup, instrumentation, and reporting are tailored to your KPIs — from share-of-voice to direct attribution.",
    points: [
      "Bespoke campaign architecture",
      "Real-time performance dashboards",
      "Outcome-led optimisation",
    ],
  },
  {
    id: "events",
    label: "Events Integration",
    body:
      "Tournaments, members' nights, private dinners — our screens slot seamlessly into the social calendar of the venues we operate in, amplifying brand presence at moments of peak attention.",
    points: [
      "Tournament & sponsorship sync",
      "Hospitality-grade integration",
      "Co-curated brand activations",
    ],
  },
];

const ease = [0.22, 1, 0.36, 1];

const OurNetwork = () => {
  const [active, setActive] = useState(tabs[0].id);
  const current = tabs.find((t) => t.id === active);

  return (
    <section
      id="network"
      data-testid="network-section"
      className="relative py-28 md:py-40 bg-ivory overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow flex items-center gap-3">
              <span className="w-10 h-px bg-brass" />
              Our Network
            </span>
            <h2 className="h-display text-4xl md:text-6xl mt-6">
              An editorial
              <br />
              <span className="italic text-brass">approach</span> to media.
            </h2>

            <div className="relative mt-10 h-72 md:h-96 overflow-hidden hairline">
              <img
                src={NETWORK_IMG}
                alt="Premium golf course"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="network-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ivory/30 via-transparent to-transparent" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-3" role="tablist" data-testid="network-tabs">
              {tabs.map((t) => {
                const isActive = active === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`network-tab-${t.id}`}
                    onClick={() => setActive(t.id)}
                    role="tab"
                    aria-selected={isActive}
                    className={`px-5 py-3 text-xs tracking-[0.18em] uppercase border transition-all duration-500 ${
                      isActive
                        ? "border-brass text-brass bg-brass/5"
                        : "border-forest-800/15 text-forest-500 hover:text-forest-900 hover:border-forest-800/40"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            <motion.div
              key={active}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease }}
              className="mt-10"
              data-testid="network-tab-content"
            >
              <h3 className="font-serif text-3xl md:text-5xl text-forest-900">
                {current.label}
              </h3>
              <p className="mt-6 text-forest-600 text-base md:text-lg leading-relaxed font-light max-w-xl">
                {current.body}
              </p>
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {current.points.map((p) => (
                  <li
                    key={p}
                    className="hairline px-5 py-4 text-sm text-forest-800 bg-ivory-soft"
                  >
                    <span className="text-brass mr-2">—</span>
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurNetwork;
